import { type components } from '~/fecom-api/schema';
import { cn } from '~/lib/utils';
import { InlineLink } from '../common/inline-link';
import { FecomImage } from '../images/fecom-image';
import { Tooltipper } from '../common/tooltipper';

export type ProductVariantsProps = {
  className?: string;
  variants?: components['schemas']['VariantType'][] | null;
  fecomMediaBaseUrl: string;
  type: 'pdp' | 'quick-view';
};

export function ProductVariants(props: ProductVariantsProps) {
  if (!props.variants || props.variants.length === 0) {
    return null;
  }

  const hasImageVariant = props.variants.some((v) => isImageVariant(v));

  if (props.type === 'quick-view' && !hasImageVariant) {
    return null;
  }

  const variants =
    // take 1st one only for quick view
    props.type === 'quick-view'
      ? props.variants.filter(isImageVariant).filter((_v, index) => index === 0)
      : props.variants;

  if (variants.length === 0) {
    return null;
  }

  return (
    <section className={cn('', props.className)}>
      <ol className="space-y-8">
        {variants?.map((variant) => {
          const selected = variant.variantValues.find((v) => v.isSelected);

          const title = (
            <>
              {variant.name}
              {selected ? <>: </> : null}
              <span className="font-bold">{selected?.name}</span>
            </>
          );
          return (
            <li key={variant.name}>
              <div className="mb-2">{title}</div>
              <ol
                className={cn(
                  'flex flex-row flex-wrap',
                  isImageVariant(variant) ? 'gap-4' : 'gap-3',
                )}
              >
                {variant.variantValues.map((variantOption) => {
                  return (
                    <VariantItem
                      key={variantOption.itemCode}
                      {...variantOption}
                      className=""
                      type={isImageVariant(variant) ? 'image' : 'text'}
                      fecomMediaBaseUrl={props.fecomMediaBaseUrl}
                    />
                  );
                })}
              </ol>
            </li>
          );
        })}
      </ol>
    </section>
  );
}

function isImageVariant(variant: components['schemas']['VariantType']) {
  return variant.name.toLowerCase().includes('colour');
}

export type VariantItemProps =
  components['schemas']['VariantType']['variantValues'][0] & {
    className?: string;
    type: 'image' | 'text';
    fecomMediaBaseUrl: string;
  };

function VariantItem({
  className,
  imageId,
  itemCode,
  isSelected,
  isValidCombination,
  name,
  type = 'text',
  fecomMediaBaseUrl,
}: VariantItemProps) {
  const renderVariantContent = () => {
    if (type === 'image' && imageId) {
      return (
        <div className="relative flex aspect-square w-10 items-center justify-center overflow-hidden rounded-lg bg-white sm:w-14">
          <FecomImage
            // we need the key here to force the image to reload when the product changes
            key={imageId}
            src={imageId ?? ''}
            className="w-full"
            fecomMediaBaseUrl={fecomMediaBaseUrl}
            alt={name ?? ''}
            width={56}
            height={56}
          />
        </div>
      );
    }
    return (
      <div className="flex h-12 min-w-16 items-center justify-center px-2 py-3 text-center text-sm">
        {name}
      </div>
    );
  };

  const link = (
    <InlineLink
      href={`/product/${itemCode.toLowerCase()}`}
      className={cn('block rounded-lg border border-border no-underline', {
        'opacity-50': !isValidCombination,
        'border-primary': isSelected,
      })}
      variant="inherit"
      scroll={false}
      prefetch={true}
    >
      {renderVariantContent()}
    </InlineLink>
  );

  if (type === 'image') {
    return <Tooltipper content={name}>{link}</Tooltipper>;
  }

  return <li key={itemCode}>{link}</li>;
}
